import { UnpaidInvoice } from '@futrli-sdk/predict';
import { API_PATHS, interpolatePathParams, REQUEST_METHODS, ROUTE_PARAMS } from '@futrli/shared-services';
import { LOADER_AREAS } from '@futrli/ui-components';
import { trackPromise } from 'react-promise-tracker';
import { makeRequestWithResponse } from './shared';

/**
 * Gets unpaid invoices or bills for a given forecast
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - forecastId for the forecast to get
 * @throws error
 * @returns Promise<UnpaidInvoice[]>
 */
export const fetchUnpaidInvoices = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<UnpaidInvoice[]> => {
  const path = interpolatePathParams(API_PATHS.UNPAID_INVOICES, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return trackPromise(
    makeRequestWithResponse<UnpaidInvoice[]>({ url: path, method: 'GET' }),
    LOADER_AREAS.predictArea
  );
};

/**
 * Updates unpaid invoices or bills for a given forecast
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - forecastId for the forecast to get
 * @param {string} billOrInvoiceId - id for the invoice or bill to be updated
 * @throws error
 * @returns Promise<Invoice>
 */
export const updateUnpaidInvoiceById = async (
  data: Partial<UnpaidInvoice>,
  workspaceId: string,
  organisationId: string,
  forecastId: string,
  billOrInvoiceId: string
): Promise<UnpaidInvoice> => {
  const path = interpolatePathParams(API_PATHS.UNPAID_INVOICE_BY_ID, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.BILL_OR_INVOICE_ID]: billOrInvoiceId
  });

  return trackPromise(
    makeRequestWithResponse<UnpaidInvoice>({ data, url: path, method: REQUEST_METHODS.PATCH }),
    LOADER_AREAS.predictArea
  );
};

/**
 * Bulk update of invoices and bills
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - forecastId for the forecast to get
 * @throws error
 * @returns Promise<Invoice>
 */
export const updateUnpaidInvoiceBulk = async (
  data: Partial<UnpaidInvoice>[],
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<void> => {
  const path = interpolatePathParams(API_PATHS.UNPAID_INVOICE_BULK, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId
  });

  return trackPromise(
    makeRequestWithResponse<void>({ data, url: path, method: REQUEST_METHODS.PATCH }),
    LOADER_AREAS.predictArea
  );
};
